import { loadScript } from '@/features/payments/gateways/script-loader'
import type { GatewayResult } from '@/features/payments/gateways/types'
import type { PaddleEnvironment } from '@/types/models'

export const PADDLE_SCRIPT = 'https://cdn.paddle.com/paddle/v2/paddle.js'

interface PaddleEvent {
  name?: string
  data?: { transaction_id?: string; status?: string }
}

interface PaddleJs {
  Initialized?: boolean
  Environment: { set(env: 'sandbox'): void }
  Initialize(options: { token: string; eventCallback: (e: PaddleEvent) => void }): void
  Checkout: {
    open(options: { transactionId: string; customer?: { email: string }; settings?: { displayMode?: 'overlay'; variant?: 'one-page' | 'multi-page' } }): void
  }
}

declare global {
  interface Window {
    Paddle?: PaddleJs
  }
}

// Paddle.js is initialised once per page; each checkout listens through the callback that is current.
let initialisedWith: string | null = null
let listener: ((e: PaddleEvent) => void) | null = null

/**
 * Loads and initialises Paddle.js once. Opening a `?_ptxn=` checkout from the URL is Paddle.js's own, once this has run.
 * Null when it can't be used: no token, the script blocked or unreachable, or the page already set up with another token
 * — Paddle.js keeps its first token (Paddle.Update cannot change it), so a second one is refused rather than recorded as
 * done (LP-542).
 */
export async function initPaddle(token: string, environment: PaddleEnvironment = 'production', onEvent?: (e: PaddleEvent) => void): Promise<PaddleJs | null> {
  if (!token) return null
  try {
    await loadScript(PADDLE_SCRIPT)
  } catch {
    return null
  }
  const Paddle = window.Paddle
  if (!Paddle) return null
  if (initialisedWith === token) {
    if (onEvent) listener = onEvent
    return Paddle
  }
  if (initialisedWith !== null || Paddle.Initialized) return null
  if (environment === 'sandbox') Paddle.Environment.set('sandbox')
  Paddle.Initialize({ token, eventCallback: (e) => listener?.(e) })
  if (Paddle.Initialized === false) return null
  initialisedWith = token
  if (onEvent) listener = onEvent
  return Paddle
}

/**
 * Opens a Paddle transaction in the overlay checkout (LP-514): `success` on checkout.completed, `cancelled` when the
 * overlay closes first, `failed` when the checkout itself errors. A declined card is not the end: Paddle keeps the
 * overlay open for another card, so settling then cancelled the transaction the buyer was about to pay. Money is only
 * trusted once the backend confirms it from the webhook.
 */
export async function openPaddle(transactionId: string | undefined, token: string | undefined, environment?: PaddleEnvironment, email?: string): Promise<GatewayResult> {
  if (!transactionId || !token) return { outcome: 'failed', message: 'Payment session missing' }

  let settle: (r: GatewayResult) => void = () => {}
  const result = new Promise<GatewayResult>((resolve) => {
    let completed = false
    settle = (r) => {
      listener = null
      resolve(r)
    }
    listener = (e) => {
      switch (e.name) {
        case 'checkout.completed':
          completed = true
          return settle({ outcome: 'success', reference: e.data?.transaction_id ?? transactionId })
        case 'checkout.closed':
          if (!completed) settle({ outcome: 'cancelled' })
          return
        case 'checkout.error':
          return settle({ outcome: 'failed', message: 'Payment failed' })
      }
    }
  })

  const Paddle = await initPaddle(token, environment)
  if (!Paddle) {
    settle({ outcome: 'failed', message: 'Payment provider unavailable' })
    return result
  }
  // The account's email, so Paddle doesn't ask for one and a different address can't end up on the invoice (LP-542).
  Paddle.Checkout.open({ transactionId, ...(email ? { customer: { email } } : {}), settings: { displayMode: 'overlay', variant: 'one-page' } })
  return result
}

/** Only for tests: forget the page's Paddle.js setup. */
export function resetPaddleForTests() {
  initialisedWith = null
  listener = null
}
